<ts-dialog title="Edit Field" (closeDialog)="dialogRef.close(false)">
  <div ts-dialog-body>
    <form class="flex flex-col justify-center gap-2" [formGroup]="bulkEditForm">
      <span class="flex flex-row items-center justify-between">
        <p>Field</p>
        <ts-select
          itemKey="name"
          itemLabel="label"
          formControlName="field"
          class="w-1/2"
          [hasGroups]="false"
          [items]="fieldsToDisplay"
          [useMousedownSelection]="true"
          [uiOptions]="{
            placeholder: 'Select a field',
            searchPlaceholder: 'Field...',
            size: 'full',
          }"
          data-testid="bulk-edit-field-select"
        ></ts-select>
      </span>
      @if (selectedField) {
        <span class="flex flex-row items-center justify-between">
          <p>Value</p>
          <div class="w-1/2">
            <ng-container [ngSwitch]="selectedField?.type">
              <ng-container *ngSwitchCase="'select'">
                <ts-select
                  [items]="getSelectItems()"
                  itemKey="key"
                  itemLabel="label"
                  formControlName="value"
                  [multi]="selectedField?.meta?.multi_select ?? false"
                  [useMousedownSelection]="true"
                  [uiOptions]="{
                    placeholder: selectedField?.meta?.multi_select ? 'Select one or more values' : 'Select a value',
                    searchPlaceholder: 'Value...',
                    size: 'full',
                  }"
                  data-testid="bulk-edit-value-input"
                ></ts-select>
              </ng-container>

              <ng-container *ngSwitchCase="'checkbox'">
                <ts-select
                  [items]="[
                    { key: true, label: 'Yes' },
                    { key: false, label: 'No' },
                  ]"
                  itemKey="key"
                  itemLabel="label"
                  formControlName="value"
                  [useMousedownSelection]="true"
                  [uiOptions]="{
                    placeholder: 'Select',
                    searchPlaceholder: 'Select...',
                    size: 'full',
                  }"
                  data-testid="bulk-edit-value-input"
                ></ts-select>
              </ng-container>

              <ng-container *ngSwitchCase="'record'">
                <mat-form-field>
                  <mat-select formControlName="value" placeholder="Select record" data-testid="bulk-edit-value-input">
                    <mat-form-field class="sticky top-0 z-10 w-full p-2 pb-0">
                      <input matInput placeholder="Search..." autocomplete="off" [formControl]="recordSearch" />
                    </mat-form-field>
                    @if (lookupLoading | async) {
                      <mat-option class="no-checkbox" disabled>
                        <mat-progress-spinner class="spinner" diameter="24" mode="indeterminate">
                        </mat-progress-spinner>
                      </mat-option>
                    }
                    <mat-option class="no-checkbox h-0" disabled> </mat-option>
                    @for (result of lookupResults | async; track result.id) {
                      <mat-option [value]="result.id">{{ result.label }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'date'">
                <mat-form-field class="datetime">
                  <input
                    matInput
                    readonly
                    [matDatepicker]="datePicker"
                    formControlName="value"
                    placeholder="Select date"
                    (dateChange)="onDateChange($event)"
                    (click)="datePicker.open()"
                    data-testid="bulk-edit-value-input"
                  />
                  <mat-datepicker #datePicker></mat-datepicker>
                  <mat-datepicker-toggle matSuffix [for]="datePicker"></mat-datepicker-toggle>
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'datetime'">
                <mat-form-field class="datetime">
                  <input
                    matInput
                    readonly
                    [matDatepicker]="dateTimePicker"
                    formControlName="value"
                    placeholder="Select date"
                    (dateChange)="onDateChange($event)"
                    (click)="dateTimePicker.open()"
                    data-testid="bulk-edit-value-input"
                  />
                  <mat-datepicker #dateTimePicker></mat-datepicker>
                  <mat-datepicker-toggle matSuffix [for]="dateTimePicker"></mat-datepicker-toggle>
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'money'">
                <mat-form-field>
                  <input
                    matInput
                    currencyMask
                    formControlName="value"
                    [options]="getCurrencyMaskOptions(selectedField)"
                    placeholder="Amount"
                    data-testid="bulk-edit-value-input"
                  />
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'percent'">
                <mat-form-field>
                  <input
                    matInput
                    type="text"
                    formControlName="value"
                    [allowNegativeNumbers]="true"
                    separatorLimit="100"
                    mask="separator.2"
                    placeholder="Percent"
                    data-testid="bulk-edit-value-input"
                  />
                  <span matSuffix>%</span>
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'number'">
                <mat-form-field>
                  <input
                    matInput
                    type="text"
                    formControlName="value"
                    [mask]="getMaskForNumberDecimal(selectedField)"
                    placeholder="Value"
                    data-testid="bulk-edit-value-input"
                  />
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'decimal'">
                <mat-form-field>
                  <input
                    matInput
                    type="text"
                    formControlName="value"
                    [mask]="getMaskForNumberDecimal(selectedField)"
                    [dropSpecialCharacters]="false"
                    placeholder="Value"
                    data-testid="bulk-edit-value-input"
                  />
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'year'">
                <mat-form-field>
                  <input
                    matInput
                    type="text"
                    formControlName="value"
                    mask="0000"
                    placeholder="Year"
                    data-testid="bulk-edit-value-input"
                  />
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'id'">
                <mat-form-field>
                  <input
                    matInput
                    type="text"
                    formControlName="value"
                    [mask]="getMaskForNumberDecimal(selectedField)"
                    placeholder="ID"
                    data-testid="bulk-edit-value-input"
                  />
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'textarea'">
                <mat-form-field>
                  <textarea
                    matInput
                    rows="4"
                    formControlName="value"
                    placeholder="Enter text"
                    data-testid="bulk-edit-value-input"
                  ></textarea>
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'text'">
                <mat-form-field>
                  <input
                    matInput
                    type="text"
                    formControlName="value"
                    placeholder="Value"
                    data-testid="bulk-edit-value-input"
                  />
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchCase="'url'">
                <mat-form-field>
                  <input
                    matInput
                    type="url"
                    formControlName="value"
                    placeholder="https://..."
                    data-testid="bulk-edit-value-input"
                  />
                </mat-form-field>
              </ng-container>

              <ng-container *ngSwitchDefault>
                <mat-form-field>
                  <input
                    matInput
                    [type]="inputType === 'url' ? 'url' : 'text'"
                    formControlName="value"
                    placeholder="Value"
                    data-testid="bulk-edit-value-input"
                  />
                </mat-form-field>
              </ng-container>
            </ng-container>
          </div>
        </span>
      }
    </form>
    @if ((ui | async)?.processing) {
      <div class="mt-3 flex items-center gap-3">
        <mat-progress-spinner diameter="20" mode="indeterminate"></mat-progress-spinner>
        <div class="text-sm">
          @for (msg of progressMessages | async; track msg) {
            <div>{{ msg }}</div>
          }
        </div>
      </div>
    }
  </div>
  <div class="ts-dialog-actions-container" ts-dialog-actions>
    @if ((ui | async).processing) {
      <ts-button variant="secondary" (buttonClick)="discard(true)" data-testid="bulk-edit-cancel-btn">Close</ts-button>
    } @else {
      <ts-button variant="secondary" (buttonClick)="discard()" data-testid="bulk-edit-cancel-btn">Cancel</ts-button>
    }

    <ts-button
      variant="primary"
      [loading]="(ui | async).processing"
      (buttonClick)="update()"
      [disabled]="bulkEditForm.invalid || (ui | async)?.processing"
      data-testid="bulk-edit-save-btn"
      >Save</ts-button
    >
  </div>
</ts-dialog>
